/**
 * Back/forward navigation guard.
 *
 * The only way to intercept a browser back press is to keep a sentinel history entry at the
 * current URL and re-push it when the user declines. Two details make or break it:
 *
 * 1. The listener must be registered BEFORE React Router creates its own history. Router
 *    listeners are attached while <BrowserRouter> first renders, and popstate listeners fire
 *    in registration order, so a guard installed from a page component runs second and can be
 *    removed mid-dispatch if the router tears that page down first. `installNavigationGuard`
 *    is therefore called from main.tsx before the root renders.
 * 2. The sentinel must preserve `window.history.state`. React Router keeps its history index
 *    and entry key in there, and overwriting it desynchronises the router's own bookkeeping.
 *
 * `useNavigationWarning` is the React-facing wrapper; prefer that over calling this directly.
 */

const SENTINEL_KEY = '__navigationGuard'

let message: string | null = null
let sentinelPushed = false
let installed = false

function pushSentinel() {
  window.history.pushState(
    { ...window.history.state, [SENTINEL_KEY]: true },
    '',
  )
  sentinelPushed = true
}

function onPopState() {
  if (message === null) return

  // This pop consumed the sentinel, whatever the user decides next.
  sentinelPushed = false

  if (window.confirm(message)) {
    // Let the navigation the user actually asked for through.
    message = null
    window.history.back()
    return
  }

  pushSentinel()
}

/** Idempotent. Call once, from main.tsx, before the root renders. */
export function installNavigationGuard() {
  if (installed) return
  installed = true
  window.addEventListener('popstate', onPopState)
}

export function armNavigationGuard(prompt: string) {
  message = prompt
  if (!sentinelPushed) pushSentinel()
}

export function disarmNavigationGuard() {
  message = null
  if (!sentinelPushed) return

  sentinelPushed = false
  // Drop the sentinel, otherwise the first back press after the operation finishes is
  // swallowed by a same-URL history entry and appears to do nothing.
  window.history.back()
}

/** Test seam: clears module state between cases. */
export function resetNavigationGuardForTests() {
  if (installed) window.removeEventListener('popstate', onPopState)
  message = null
  sentinelPushed = false
  installed = false
}
